import moment from "moment";

/**
 * Repeat settings for "Assign a task" — the web copy of the mobile app's
 * app/locations/_components/repeat-options.tsx. Each preset maps onto a
 * backend rule (teamplayer_backend/utils/recurrence.js), which re-validates.
 *
 * Settings shape:
 *   {
 *     preset: "daily" | "weekdays" | "weekends" | "select_days" | "biweekly" | "custom",
 *     byWeekday: [0..6],     // select_days / biweekly; 0 = Sunday
 *     endDate: Date | null,  // null = a month after the start (within the cap)
 *     customDates: ["YYYY-MM-DD"], // custom only
 *   }
 */

/** A series may run for at most this many months (backend MAX_SPAN_MONTHS). */
export const MAX_REPEAT_MONTHS = 6;

const WEEKDAYS = [1, 2, 3, 4, 5];
const WEEKENDS = [0, 6];
const DAY_FMT = "YYYY-MM-DD";
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const ORDINALS = ["first", "second", "third", "fourth", "fifth"];

export const REPEAT_PRESETS = [
  { value: "daily", label: "Daily" },
  { value: "weekdays", label: "Every weekday (Mon–Fri)" },
  { value: "weekends", label: "Every weekend (Sat–Sun)" },
  { value: "select_days", label: "Select days" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "custom", label: "Custom dates" },
];

export const defaultRepeatSettings = (startDate) => ({
  preset: "select_days",
  byWeekday: [startDate.getDay()],
  endDate: null,
  customDates: [],
});

export const maxRepeatEndDate = (fromDate) =>
  moment(fromDate).startOf("day").add(MAX_REPEAT_MONTHS, "months").toDate();

/**
 * Editing a series already under way: the end can't be before today and the
 * 6-month cap counts from today; the start date stays the anchor. For a new
 * series the floor is the start date.
 */
export const floorFor = (startDate, floorDate) =>
  floorDate && moment(floorDate).isAfter(startDate, "day") ? floorDate : startDate;

export const effectiveEndDate = (settings, floor) => {
  if (settings.endDate) return settings.endDate;
  const oneMonth = moment(floor).startOf("day").add(1, "month").toDate();
  const max = maxRepeatEndDate(floor);
  return oneMonth > max ? max : oneMonth;
};

/**
 * Days a custom series can be picked from, up to 6 months out: from today
 * for a new series (it starts on its first picked day), from the floor on
 * edit (the backend drops anything earlier).
 */
export const customRange = (startDate, floorDate) => {
  const min = moment(floorDate ? floorFor(startDate, floorDate) : new Date()).startOf("day");
  return { min, max: moment(maxRepeatEndDate(min.toDate())) };
};

export const sortedCustomDates = (settings, startDate, floorDate) => {
  const { min, max } = customRange(startDate, floorDate);
  return [...new Set(settings.customDates)]
    .filter((d) => {
      const day = moment(d, DAY_FMT);
      return !day.isBefore(min, "day") && !day.isAfter(max, "day");
    })
    .sort();
};

export const isWeekly = (preset) => preset !== "daily" && preset !== "custom";

/** Weekdays a weekly preset repeats on. */
export const weekdaysFor = (settings) => {
  if (settings.preset === "weekdays") return WEEKDAYS;
  if (settings.preset === "weekends") return WEEKENDS;
  return [...settings.byWeekday].sort();
};

/** The first picked date for custom (keeping `fallback`'s time), else `fallback`. */
export const repeatStartDate = (settings, fallback) => {
  if (settings.preset !== "custom" || settings.customDates.length === 0) return fallback;
  const first = [...settings.customDates].sort()[0];
  const time = moment(fallback);
  return moment(first, DAY_FMT)
    .set({ hour: time.hour(), minute: time.minute(), second: 0 })
    .toDate();
};

/** Client copy of the backend's matchesPattern (ignoring "after N" counts). */
const ruleMatches = (rule, date) => {
  const start = moment(rule.start_date, DAY_FMT);
  if (date.isBefore(start, "day")) return false;
  const interval = rule.interval || 1;
  switch (rule.freq) {
    case "dates":
      return (rule.dates || []).includes(date.format(DAY_FMT));
    case "day":
      return date.diff(start, "days") % interval === 0;
    case "week": {
      if (!(rule.by_weekday || []).includes(date.day())) return false;
      const startWeek = start.clone().subtract(start.day(), "days");
      const dateWeek = date.clone().subtract(date.day(), "days");
      return (dateWeek.diff(startWeek, "days") / 7) % interval === 0;
    }
    case "month": {
      const months = (date.year() - start.year()) * 12 + (date.month() - start.month());
      if (months % interval !== 0) return false;
      const mode = rule.month_mode || "day";
      if (mode === "day") {
        const days = rule.by_monthday?.length ? rule.by_monthday : [start.date()];
        if (days.includes(date.date())) return true;
        return days.includes(-1) && date.clone().add(1, "day").month() !== date.month();
      }
      if (date.day() !== start.day()) return false;
      if (mode === "nth_weekday") return Math.ceil(date.date() / 7) === Math.ceil(start.date() / 7);
      return date.clone().add(7, "days").month() !== date.month();
    }
    default:
      return false;
  }
};

const countVisits = (rule, from, to) => {
  let count = 0;
  for (const day = moment(from).startOf("day"); !day.isAfter(to, "day"); day.add(1, "day")) {
    if (ruleMatches(rule, day)) count += 1;
  }
  return count;
};

/** The `recurrence` body for POST /locations/create and PUT /locations/series/:id. */
export const toRecurrencePayload = (settings, startDate, floorDate) => {
  if (settings.preset === "custom") {
    const dates = sortedCustomDates(settings, startDate, floorDate);
    return {
      freq: "dates",
      interval: 1,
      by_weekday: [],
      dates,
      ends: { type: "on", until: dates[dates.length - 1] },
    };
  }
  const ends = {
    type: "on",
    until: moment(effectiveEndDate(settings, floorFor(startDate, floorDate))).format(DAY_FMT),
  };
  if (settings.preset === "daily") {
    return { freq: "day", interval: 1, by_weekday: [], ends };
  }
  return {
    freq: "week",
    interval: settings.preset === "biweekly" ? 2 : 1,
    by_weekday: weekdaysFor(settings),
    ends,
  };
};

/** Returns an error message for the first problem, or null when valid. */
export const validateRepeatSettings = (settings, startDate, floorDate) => {
  if (settings.preset === "custom") {
    return sortedCustomDates(settings, startDate, floorDate).length === 0
      ? "Pick at least one date to repeat on"
      : null;
  }
  if (isWeekly(settings.preset) && weekdaysFor(settings).length === 0) {
    return "Select at least one day to repeat on";
  }
  const floor = floorFor(startDate, floorDate);
  const endDate = effectiveEndDate(settings, floor);
  if (moment(endDate).isBefore(floor, "day")) {
    return "End date can't be before the start date";
  }
  if (moment(endDate).isAfter(maxRepeatEndDate(floor), "day")) {
    return `A repeat can run for at most ${MAX_REPEAT_MONTHS} months`;
  }
  const rule = {
    ...toRecurrencePayload(settings, startDate, floorDate),
    start_date: moment(startDate).format(DAY_FMT),
  };
  if (countVisits(rule, floor, endDate) === 0) {
    return "No visits fall between the start and end dates";
  }
  return null;
};

const sameDays = (a = [], b) =>
  a.length === b.length && [...a].sort().every((d, i) => d === [...b].sort()[i]);

/** A saved rule the simple editor can't show as it is (monthly, every 3+ days/weeks). */
export const isLegacyRule = (rule) =>
  rule.freq === "month" ||
  (rule.freq === "day" && (rule.interval || 1) !== 1) ||
  (rule.freq === "week" && (rule.interval || 1) > 2);

/** Pre-fills the editor from a saved rule. */
export const settingsFromRule = (rule) => {
  const start = moment(rule.start_date, DAY_FMT);
  const interval = rule.interval || 1;
  const days = rule.by_weekday || [];
  let preset = "select_days";
  if (rule.freq === "dates") preset = "custom";
  else if (rule.freq === "day" && interval === 1) preset = "daily";
  else if (rule.freq === "week" && interval === 2) preset = "biweekly";
  else if (rule.freq === "week" && interval === 1 && sameDays(days, WEEKDAYS)) preset = "weekdays";
  else if (rule.freq === "week" && interval === 1 && sameDays(days, WEEKENDS)) preset = "weekends";

  // "after N" becomes the date of the Nth visit; legacy "never" the default.
  const ends = rule.ends || { type: "never" };
  let endDate = null;
  if (ends.type === "on" && ends.until) {
    endDate = moment(ends.until, DAY_FMT).toDate();
  } else if (ends.type === "after" && ends.count) {
    const cap = moment(maxRepeatEndDate(moment().startOf("day").toDate()));
    let seen = 0;
    for (const day = start.clone(); !day.isAfter(cap, "day"); day.add(1, "day")) {
      if (ruleMatches(rule, day)) seen += 1;
      if (seen === ends.count) {
        endDate = day.toDate();
        break;
      }
    }
  }

  return {
    preset,
    byWeekday: rule.freq === "week" && days.length ? [...days] : [start.day()],
    endDate,
    customDates: rule.freq === "dates" ? [...(rule.dates || [])] : [],
  };
};

const shortDate = (day) => moment(day, DAY_FMT).format("D MMM YYYY");

const describeMonthdays = (days) => {
  const hasLast = days.includes(-1);
  const numbers = days.filter((d) => d !== -1).sort((a, b) => a - b);
  if (numbers.length === 0) return "on the last day";
  if (numbers.length === 1 && !hasLast) return `on day ${numbers[0]}`;
  const list = numbers.join(", ");
  return hasLast ? `on days ${list} and the last day` : `on days ${list}`;
};

/** One-line summary, e.g. "Every 2 weeks on Mon, Fri · until 21 Mar 2027". */
export const describeRule = (rule) => {
  if (rule.freq === "dates") {
    const dates = [...(rule.dates || [])].sort();
    if (dates.length === 0) return "On 0 picked dates";
    const pattern =
      dates.length === 1 ? "On 1 picked date" : `On ${dates.length} picked dates`;
    const range =
      dates.length === 1
        ? shortDate(dates[0])
        : `${shortDate(dates[0])} – ${shortDate(dates[dates.length - 1])}`;
    return `${pattern} · ${range}`;
  }

  const start = moment(rule.start_date, DAY_FMT);
  const n = Number(rule.interval) || 1;
  let pattern;
  if (rule.freq === "day") {
    pattern = n === 1 ? "Every day" : `Every ${n} days`;
  } else if (rule.freq === "week") {
    const weekdays = rule.by_weekday || [];
    const days = [...weekdays]
      .sort()
      .map((d) => WEEKDAY_SHORT[d])
      .join(", ");
    if (n === 1 && sameDays(weekdays, WEEKDAYS)) pattern = "Every weekday";
    else if (n === 1 && sameDays(weekdays, WEEKENDS)) pattern = "Every weekend";
    else pattern = n === 1 ? `Every week on ${days}` : `Every ${n} weeks on ${days}`;
  } else {
    const weekday = WEEKDAY_NAMES[start.day()];
    const mode = rule.month_mode || "day";
    const on =
      mode === "nth_weekday"
        ? `on the ${ORDINALS[Math.ceil(start.date() / 7) - 1]} ${weekday}`
        : mode === "last_weekday"
          ? `on the last ${weekday}`
          : describeMonthdays(rule.by_monthday?.length ? rule.by_monthday : [start.date()]);
    pattern = n === 1 ? `Every month ${on}` : `Every ${n} months ${on}`;
  }

  const ends = rule.ends || { type: "never" };
  const end =
    ends.type === "on" && ends.until
      ? `until ${shortDate(ends.until)}`
      : ends.type === "after" && ends.count
        ? `${ends.count} times`
        : "no end date";
  return `${pattern} · ${end}`;
};

/** Summary of the settings as entered, or null while no days/dates are picked. */
export const describeSettings = (settings, startDate, floorDate) => {
  if (
    settings.preset === "custom" &&
    sortedCustomDates(settings, startDate, floorDate).length === 0
  ) {
    return null;
  }
  if (isWeekly(settings.preset) && weekdaysFor(settings).length === 0) return null;
  return describeRule({
    ...toRecurrencePayload(settings, startDate, floorDate),
    start_date: moment(startDate).format(DAY_FMT),
  });
};
